function ChevronLeft({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

function ChevronRight({ className = 'w-4 h-4' }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

const squareBtn =
  'w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-xs font-medium font-sf-pro-display transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none';

const squareIdle = 'bg-[#EBF4EA] text-[#868686] hover:brightness-95';
const squareActive = 'bg-[#50AB3D] text-[#EBF4EA]';

/**
 * Shared pagination — Figma: Previous | page squares | Next
 */
export function Pagination({
  currentPage,
  page,
  totalCount,
  totalPages: propTotalPages,
  itemsPerPage = 10,
  onChange,
  className = '',
}) {
  const activePage = currentPage ?? page ?? 1;
  const totalPages =
    propTotalPages != null
      ? Math.max(1, propTotalPages)
      : totalCount != null
        ? Math.max(1, Math.ceil(Math.max(0, totalCount) / itemsPerPage))
        : 1;

  const hasData = totalCount != null ? totalCount > 0 : totalPages > 0;
  const canPrev = activePage > 1 && hasData;
  const canNext = activePage < totalPages && hasData;

  const getPageNumbers = (current, total) => {
    if (total <= 0) return [];
    const pages = [];
    pages.push(1);

    let start = Math.max(2, current - 1);
    let end = Math.min(total - 1, current + 1);

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total - 1) {
      pages.push('...');
    }

    if (total > 1) {
      pages.push(total);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers(activePage, totalPages);

  return (
    <div
      className={`w-full mt-auto px-2 sm:px-4 py-2 sm:py-2.5 font-sf-pro-display border-t border-[#EBF4EA] ${className}`.trim()}
    >
      <div className="flex items-center justify-between gap-1.5 sm:gap-2.5 w-full min-w-0">
        {/* Previous */}
        <div className="shrink-0">
          <button
            type="button"
            onClick={() => onChange(Math.max(1, activePage - 1))}
            disabled={!canPrev}
            aria-label="Previous Page"
            className="inline-flex h-7 sm:h-8 items-center justify-center gap-1 sm:gap-1.5 rounded-lg bg-[#F5F5F5] px-2.5 sm:px-3 text-xs font-medium text-[#868686] hover:bg-[#EBF4EA] hover:text-[#50AB3D] disabled:opacity-50 disabled:pointer-events-none transition-colors focus:outline-none shrink-0"
          >
            <ChevronLeft className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden min-[420px]:inline">Previous</span>
          </button>
        </div>

        {/* Center page controls */}
        <div className="flex items-center justify-center gap-1 sm:gap-1.5 min-w-0 overflow-x-auto no-scrollbar py-0.5">
          {pageNumbers.map((num, i) =>
            num === '...' ? (
              <span
                key={`dots-${i}`}
                className={`${squareBtn} ${squareIdle} pointer-events-none shrink-0`}
              >
                ...
              </span>
            ) : (
              <button
                type="button"
                key={num}
                onClick={() => onChange(num)}
                aria-current={activePage === num ? 'page' : undefined}
                className={`${squareBtn} ${
                  activePage === num ? squareActive : squareIdle
                } shrink-0`}
              >
                {num}
              </button>
            ),
          )}
        </div>

        {/* Next */}
        <div className="shrink-0">
          <button
            type="button"
            onClick={() => onChange(Math.min(totalPages, activePage + 1))}
            disabled={!canNext}
            aria-label="Next Page"
            className="inline-flex h-7 sm:h-8 items-center justify-center gap-1 sm:gap-1.5 rounded-lg bg-[#F5F5F5] px-2.5 sm:px-3 text-xs font-medium text-[#868686] hover:bg-[#EBF4EA] hover:text-[#50AB3D] disabled:opacity-50 disabled:pointer-events-none transition-colors focus:outline-none shrink-0"
          >
            <span className="hidden min-[420px]:inline">Next</span>
            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
          </button>
        </div>
      </div>
    </div>
  );
}
